'use client';

import { Language } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useLocale } from 'next-intl';
import { useContext, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext, type CartFormValues } from '@/entities/cart/model/cart-context';
import { useCartStore } from '@/entities/cart/model/cart-store';
import { useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { QueryApi } from '@/features/query/api';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';

const EMPTY_PAYMENT: CartFormValues['paymentMethod'] = { id: null };

const EMPTY_TIME: CartFormValues['time'] = { type: 'earliest', timed: null };

const EMPTY_TIPS: CartFormValues['tips'] = {};

/**
 * Single bridge: RHF + queries → computeCartTotals → zustand.
 * Mount once under CartProvider. Always recomputes so getItemPrices works
 * on product pages even before cart form fields are watched.
 */
export const CartTotalsSync = () => {
    const { formMethods } = useContext(CartContext);
    const recompute = useCartStore((s) => s.recompute);
    const [selectedCurrency] = useSelectedCurrency();
    const locale = useLocale();
    const userLanguage = locale.toUpperCase().replace('-', '_') as Language;
    const cartMenuData = useStorefrontCartMenuData();

    const deliveryMethods = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const paymentMethods = useQuery(QueryApi.PaymentMethods.queries.PaymentMethods);
    const venue = useQuery(QueryApi.Venue.queries.Venue);

    // Granular delivery fields — avoid re-running on unrelated object identity churn.
    const [
        items,
        deliveryMethodId,
        deliveryType,
        deliveryManual,
        deliveryAddressId,
        deliveryAddress,
        paymentMethod,
        time,
        tips,
        topUpOption,
    ] = useWatch({
        control: formMethods.control,
        name: [
            'items',
            'deliveryMethod.id',
            'deliveryMethod.type',
            'deliveryMethod.manual',
            'deliveryMethod.addressId',
            'deliveryMethod.address',
            'paymentMethod',
            'time',
            'tips',
            'topUpOption',
        ],
    });

    const resolvedDelivery = useMemo(
        (): CartFormValues['deliveryMethod'] => ({
            id: deliveryMethodId ?? null,
            type: deliveryType ?? null,
            manual: deliveryManual ?? false,
            addressId: deliveryAddressId ?? null,
            address: deliveryAddress ?? null,
        }),
        [deliveryMethodId, deliveryType, deliveryManual, deliveryAddressId, deliveryAddress],
    );

    const lat = resolvedDelivery.address?.lat;
    const lon = resolvedDelivery.address?.lon;
    const resolvedTime = time ?? EMPTY_TIME;
    const timeSlotDate =
        resolvedTime.type === 'timed' && resolvedTime.timed?.date && resolvedTime.timed?.timeStart
            ? dayjs(`${resolvedTime.timed.date}T${resolvedTime.timed.timeStart}`)
            : null;

    const selectedDeliveryMethodId = resolvedDelivery.id ?? '';
    const ecoDiscountQuery = useQuery({
        ...QueryApi.Order.queries.TimedOrderDiscount({
            deliveryMethodId: selectedDeliveryMethodId,
            dateTime: timeSlotDate?.toISOString() ?? '',
            latitude: parseFloat(lat ?? ''),
            longitude: parseFloat(lon ?? ''),
        }),
        enabled: !!selectedDeliveryMethodId && !!lat && !!lon && !!timeSlotDate?.isValid(),
    });

    useEffect(() => {
        recompute({
            form: {
                items: items ?? [],
                deliveryMethod: resolvedDelivery,
                paymentMethod: paymentMethod ?? EMPTY_PAYMENT,
                time: resolvedTime,
                tips: tips ?? EMPTY_TIPS,
                topUpOption: topUpOption ?? 'pay',
            },
            menuLists: cartMenuData,
            deliveryMethods: deliveryMethods.data,
            paymentMethods: paymentMethods.data,
            venue: venue.data ?? null,
            selectedCurrency,
            userLanguage,
            ecoDiscount: ecoDiscountQuery.data ?? null,
        });
    }, [
        recompute,
        items,
        resolvedDelivery,
        paymentMethod,
        resolvedTime,
        tips,
        topUpOption,
        cartMenuData,
        deliveryMethods.data,
        paymentMethods.data,
        venue.data,
        selectedCurrency,
        userLanguage,
        ecoDiscountQuery.data,
    ]);

    return null;
};
